import { Link } from "gatsby";
import React, { FC, useContext } from "react";
import { useCoursesContext } from "../../../context/CoursesContext";
import { PageContext } from "../../../context/PageContext";
import useTranslations from "../../../hooks/useTranslations";
import { CourseFormat } from "../../../models/courses/CourseFormat";
import { buildInternalUrl } from "../../../utilities/routes/urlBuilder";
import Button, { ButtonSize } from "../../Buttons/Button";
import { getDateRender } from "../CourseBookingForm/helpers";
import { getCourseFormat } from "../CourseCard/helpers";
import { CourseItemData } from "../CoursePossibilitiesFilters/CoursePossibilitiesFilters";

interface CourseInfoBlockItemProps {
  index: number;
  item: CourseItemData;
}

const CourseInfoBlockItem: FC<CourseInfoBlockItemProps> = ({ index, item }) => {
  const translations = useTranslations();

  const pageContext = useContext(PageContext);
  const { coursePageData } = pageContext;
  const { course, landing } = coursePageData ?? {};

  const {
    state: { courseListSlug }
  } = useCoursesContext();

  const buttonLink = buildInternalUrl(
    {
      i18nLang: pageContext?.i18nLang,
      _type: "courseBookingForm"
    },
    null,
    undefined,
    `/${landing?.slug.current}/${courseListSlug}/${course?.slug.current}/booking?subCourseId=${item?.id}`,
    true
  );

  const address = item?.location?.address;
  const city = item?.location?.city;

  const formattedAddress = [
    address,
    city && address
      ? address.toLowerCase().includes(city.toLowerCase())
        ? null
        : city
      : null
  ]
    .filter(Boolean)
    .join(", ");

  const isOnlineCourse = item.format === CourseFormat.Online;
  const isButtonDisabled = isOnlineCourse ? false : Number(item?.numberOfPlaces) <= 0;

  return (
    <div
      className="bg-[var(--blue-20)] p-5 md:p-[1.875rem] mb-5 rounded-lg last-of-type:mb-0 flex gap-10 md:gap-2 items-start md:items-center flex-col md:flex-row"
      key={index}
    >
      <div className="md:flex-grow">
        {item?.name && (
          <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
            <span className="font-bold">{translations?.courseTraining}: </span>
            {item?.name}
          </p>
        )}
        {!isOnlineCourse && (
          <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
            <span className="font-bold">{translations?.courseDetailDateAndTime}: </span>
            {getDateRender(item?.start_date, item?.end_date)}
          </p>
        )}
        {!isOnlineCourse && !!formattedAddress && (
          <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
            <span className="font-bold">{translations?.courseDetailLocation}: </span>
            {formattedAddress}
          </p>
        )}
        <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
          <span className="font-bold">{translations?.courseDetailPrice}: </span>
          {item.price}
        </p>
        {!isOnlineCourse && (
          <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
            <span className="font-bold">{translations?.courseDetailNumberOfSeats}: </span>
            {item.numberOfPlaces} st
          </p>
        )}
        <p className="text-neutral-100 m-0 text-[1rem] md:text-2xl">
          <span className="font-bold">{translations?.courseFormat}: </span>
          {getCourseFormat(item.format || "", translations)}
        </p>
      </div>
      <Link to={buttonLink} className="text-nowrap">
        <Button
          color="primary-light"
          size={ButtonSize.Normal}
          className="self-center md:self-auto w-full md:w-auto "
          disabled={isButtonDisabled}
        >
          {translations?.bookCourseNowCta}
        </Button>
      </Link>
    </div>
  );
};

export default CourseInfoBlockItem;
